/* Mughal arch image reveal.

   The photograph enters as a four-centred pishtaq arch — the iwan
   profile of the Taj gateway: near-vertical jambs, a long flat-ish
   haunch, and two radii a side meeting at a true point — then opens
   into the full frame as it scrolls into view.

   Both shapes are the same fifteen points in the same order, so the
   morph is a straight numeric interpolation: no path libraries, and
   no reliance on CSS `d` animation, which Safari and Firefox only
   picked up recently.

   Coordinates are objectBoundingBox units (0..1), so the clip scales
   with the image at any width without recalculation.

   Same two rules as az-reveal: nothing is clipped unless JS runs, and
   anything already on screen is left alone rather than snapping into
   an arch the reader has already seen as a rectangle.
*/

const REDUCED = '(prefers-reduced-motion: reduce)';

// prettier-ignore
const ARCH = [
  0.18, 1,      0.18, 0.44,
  0.18, 0.21,   0.29, 0.085,  0.42, 0.045,
  0.455, 0.031, 0.484, 0.017, 0.50, 0,
  0.516, 0.017, 0.545, 0.031, 0.58, 0.045,
  0.71, 0.085,  0.82, 0.21,   0.82, 0.44,
  0.82, 1,
];

// The open frame. The x values in the middle deliberately match the
// arch's, so the apex and haunch points travel almost straight up
// instead of sliding sideways while the shape unfurls.
// prettier-ignore
const FRAME = [
  0, 1,         0, 0,
  0, 0,         0.21, 0,      0.42, 0,
  0.455, 0,     0.484, 0,     0.50, 0,
  0.516, 0,     0.545, 0,     0.58, 0,
  0.79, 0,      1, 0,         1, 0,
  1, 1,
];

// The arch holds before it opens. With a plain ease-out the frame was
// already 59% open a fifth of the way in, so the shape the whole effect
// exists for flashed past in about 300ms. A short dwell plus an ease
// that starts slow keeps it legible through the first third.
const DWELL = 240;
const DURATION = 1250;
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const p = (v, i) => `${v[i * 2].toFixed(4)},${v[i * 2 + 1].toFixed(4)}`;
const toPath = (v) =>
  `M${p(v, 0)}L${p(v, 1)}` +
  `C${p(v, 2)} ${p(v, 3)} ${p(v, 4)}` +
  `C${p(v, 5)} ${p(v, 6)} ${p(v, 7)}` +
  `C${p(v, 8)} ${p(v, 9)} ${p(v, 10)}` +
  `C${p(v, 11)} ${p(v, 12)} ${p(v, 13)}` +
  `L${p(v, 14)}Z`;

let uid = 0;

class AzArch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready !== undefined) return;
    this.dataset.ready = '';

    if (window.matchMedia(REDUCED).matches) return;
    if (!('IntersectionObserver' in window)) return;
    if (this.getBoundingClientRect().top < window.innerHeight * 0.9) return;

    const id = `az-arch-${++uid}`;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.classList.add('az-arch__defs');

    const clip = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
    clip.setAttribute('id', id);
    clip.setAttribute('clipPathUnits', 'objectBoundingBox');

    this.path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    this.path.setAttribute('d', toPath(ARCH));

    clip.appendChild(this.path);
    svg.appendChild(clip);
    this.appendChild(svg);

    this.style.clipPath = `url(#${id})`;
    this.setAttribute('data-arch', '');

    this.io = new IntersectionObserver(this.onIntersect, {
      rootMargin: '0px 0px -15% 0px',
    });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.io.disconnect();
    this.setAttribute('data-open', '');
    this.start = null;
    this.raf = requestAnimationFrame(this.step);
  };

  step = (now) => {
    if (this.start === null) this.start = now;
    const t = Math.min(1, Math.max(0, now - this.start - DWELL) / DURATION);
    const e = easeInOutCubic(t);

    const v = ARCH.map((from, i) => from + (FRAME[i] - from) * e);
    this.path.setAttribute('d', toPath(v));

    if (t < 1) {
      this.raf = requestAnimationFrame(this.step);
      return;
    }

    // Drop the clip once it is a rectangle again. It leaves the edges
    // crisp rather than clip-antialiased, and stops the compositor
    // carrying a mask it no longer needs.
    this.raf = null;
    this.style.clipPath = '';
    this.removeAttribute('data-arch');
    this.querySelector('.az-arch__defs')?.remove();
  };
}

if (!customElements.get('az-arch')) {
  customElements.define('az-arch', AzArch);
}
